/**
 * A hash router in 40 lines. react-router is not on CLAUDE.md §4's dependency
 * list, and navigation here is a flat list of screens with one detail view —
 * not enough to earn a dependency.
 */
import { useEffect, useState } from "react";

export type RouteId =
  | "dashboard"
  | "outstanding"
  | "ledgers"
  | "ledger"
  | "voucher"
  | "stock"
  | "daybook"
  | "sales"
  | "search"
  | "reports"
  | "more"
  | "balance-sheet"
  | "profit-loss"
  | "trial-balance"
  | "cash-book"
  | "ratios"
  | "graphs"
  | "insights"
  | "dormant"
  | "top-customers"
  | "top-items"
  | "dead-stock"
  | "cost-centres"
  | "godowns"
  | "batches"
  | "settings";

export type Route = { id: RouteId; param?: string };

/**
 * `nav` marks the bottom-bar destinations.
 *
 * It used to be nine, which meant a horizontally scrolling strip of pills where
 * half the app was off-screen. SIX fixed targets is the current limit: at a
 * 360px viewport that is 60px each, which still fits an icon and a short label
 * without scrolling. It is the ceiling, not a starting point — a seventh would
 * have to displace one of these rather than join them.
 */
export const ROUTES: Array<{ id: RouteId; title: string; short: string; nav: boolean }> = [
  { id: "dashboard", title: "Overview", short: "Home", nav: true },
  { id: "outstanding", title: "Outstanding", short: "Owed", nav: true },
  { id: "ledgers", title: "Ledgers", short: "Ledgers", nav: true },
  { id: "graphs", title: "Graphs", short: "Graphs", nav: true },
  { id: "reports", title: "Reports", short: "Reports", nav: true },
  { id: "more", title: "More", short: "More", nav: true },

  { id: "ledger", title: "Ledger", short: "Ledger", nav: false },
  { id: "voucher", title: "Voucher", short: "Voucher", nav: false },
  { id: "stock", title: "Stock Summary", short: "Stock", nav: false },
  { id: "daybook", title: "Day Book", short: "Day Book", nav: false },
  { id: "sales", title: "Sales Register", short: "Sales", nav: false },
  { id: "search", title: "Search", short: "Search", nav: false },
  { id: "balance-sheet", title: "Balance Sheet", short: "BS", nav: false },
  { id: "profit-loss", title: "Profit & Loss", short: "P&L", nav: false },
  { id: "trial-balance", title: "Trial Balance", short: "TB", nav: false },
  { id: "cash-book", title: "Cash & Bank", short: "Cash", nav: false },
  { id: "ratios", title: "Ratios", short: "Ratios", nav: false },
  { id: "insights", title: "Insights", short: "Insights", nav: false },
  { id: "dormant", title: "Stopped buying", short: "Dormant", nav: false },
  { id: "top-customers", title: "Top Customers", short: "Top", nav: false },
  { id: "top-items", title: "Top Items", short: "Items", nav: false },
  { id: "dead-stock", title: "Not Selling", short: "Dead", nav: false },
  { id: "cost-centres", title: "Cost Centres", short: "Cost", nav: false },
  { id: "godowns", title: "Godowns", short: "Godowns", nav: false },
  { id: "batches", title: "Batches & Expiry", short: "Batches", nav: false },
  { id: "settings", title: "Settings", short: "Settings", nav: false },
];

const IDS = new Set(ROUTES.map((r) => r.id));

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "");
  const [id, ...rest] = raw.split("/");
  if (!id || !IDS.has(id as RouteId)) return { id: "dashboard" };
  const param = rest.length > 0 ? decodeURIComponent(rest.join("/")) : undefined;
  return param === undefined ? { id: id as RouteId } : { id: id as RouteId, param };
}

export function href(route: Route): string {
  return route.param === undefined
    ? `#/${route.id}`
    : `#/${route.id}/${encodeURIComponent(route.param)}`;
}

export function navigate(route: Route): void {
  window.location.hash = href(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  return route;
}
